import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Button,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  IconButton,
  useTheme,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
  InputAdornment,
  useMediaQuery
} from '@mui/material';
import { 
  ContentCopy as CopyIcon,
  Chat as ChatIcon, 
  Code as CodeIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  SmartToy as SmartToyIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  UploadFile as UploadFileIcon,
  Article as ArticleIcon,
  Language as LanguageIcon,
  UpdateDisabled as UpdateDisabledIcon,
  Web as WebIcon,
  Dashboard as DashboardIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  AutoFixHigh as AutoFixHighIcon,
  Share as ShareIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon, // Add CheckIcon to the import list
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// Chat Preview Component
const ChatPreview = ({ bot }) => {
  const theme = useTheme();
  const { token } = useAuth();
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([
    { role: 'bot', content: bot?.customizations?.welcomeMessage || 'Hello! How can I help you today?' }
  ]);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const chatBoxRef = React.useRef(null);

  // Enhanced formatting function for responses
  const formatResponse = (response) => {
    if (!response) return '';
    
    // Replace newlines with <br>
    let formatted = response.replace(/\n/g, '<br>');
    
    // Format bold text: **text** or __text__ to <strong>text</strong>
    formatted = formatted.replace(/(\*\*|__)(.*?)\1/g, '<strong>$2</strong>');
    
    // Format italic text: *text* or _text_ to <em>text</em>
    formatted = formatted.replace(/(\*|_)(.*?)\1/g, '<em>$2</em>');
    
    // Format code blocks with ```
    formatted = formatted.replace(/```(.*?)```/gs, '<pre><code>$1</code></pre>');
    
    return formatted;
  };

  const handleSend = async () => {
    if (!message.trim()) return;

    // Add user message to chat
    const userMessage = message;
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setMessage('');
    setSending(true);

    try {
      // Send message to API
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/chat/${bot.id}`,
        { message: userMessage },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Add bot response
      setChatHistory(prev => [
        ...prev,
        { role: 'bot', content: formatResponse(response.data.response) }
      ]);
    } catch (error) {
      console.error('Error sending message:', error);
      setChatHistory(prev => [
        ...prev,
        {
          role: 'bot',
          content: 'Sorry, I encountered an error processing your message. Please try again later.'
        }
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleTestChat = () => {
    navigate(`/bots/${bot.id}/test`);
  };

  return (
    <Card
      sx={{
        maxWidth: '500px',
        minHeight: '650px',
        mx: 'auto',
        boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
        borderRadius: 3,
        overflow: 'hidden'
      }}
    >
      {/* Chat header */}
      <Box
        sx={{
          p: 2,
          backgroundColor: bot.customizations?.primaryColor || theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box display="flex" alignItems="center">
          <Avatar
            sx={{
              backgroundColor: 'white',
              color: bot.customizations?.primaryColor || theme.palette.primary.main,
              mr: 2
            }}
          >
            <SmartToyIcon />
          </Avatar>
          <Typography variant="h6">{bot.name}</Typography>
        </Box>
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          onClick={handleTestChat}
          sx={{
            color: 'white',
            borderColor: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          Test Chat
        </Button>
      </Box>

      {/* Chat messages */}
      <Box
        sx={{
          height: '600px',
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          backgroundColor: '#f5f7fb'
        }}
        ref={chatBoxRef}
      >
        {chatHistory.map((msg, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              mb: 1
            }}
          >
            <Box
              sx={{
                maxWidth: '80%',
                p: 1.5,
                borderRadius: 2,
                backgroundColor: msg.role === 'user' ? 'white' : bot.customizations?.primaryColor || theme.palette.primary.main,
                color: msg.role === 'user' ? 'text.primary' : 'white',
                boxShadow: '0 2px 5px rgba(0,0,0,0.08)'
              }}
              dangerouslySetInnerHTML={{ __html: msg.content }} // Render formatted response
            />
          </Box>
        ))}

        {sending && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Box sx={{ display: 'flex', ml: 1 }}>
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
            </Box>
          </Box>
        )}
      </Box>

      {/* Chat input */}
      <Box sx={{ p: 2, display: 'flex', borderTop: '1px solid #eee' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type a message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSend()}
          disabled={sending}
          size="small"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={!message.trim() || sending}
          sx={{
            ml: 1,
            backgroundColor: bot.customizations?.primaryColor || theme.palette.primary.main
          }}
        >
          Send
        </Button>
      </Box>

      <style jsx global>{`
        .typing-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${bot.customizations?.primaryColor || theme.palette.primary.main};
          margin-right: 4px;
          animation: typing 1.4s infinite both;
        }
        .typing-dot:nth-child(2) {
          animation-delay: 0.2s;
        }
        .typing-dot:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes typing {
          0%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </Card>
  );
};

const BotDetails = () => {
  const theme = useTheme();
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [embedCode, setEmbedCode] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(""); // Add missing error state
  
  // Knowledge base states
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [websiteDialogOpen, setWebsiteDialogOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [scrapingWebsite, setScrapingWebsite] = useState(false);
  const [websiteError, setWebsiteError] = useState('');
  const [trainingBot, setTrainingBot] = useState(false);
  const [trainingComplete, setTrainingComplete] = useState(false);
  
  // Add state for assistant files
  const [assistantFiles, setAssistantFiles] = useState([]);
  const [loadingAssistantFiles, setLoadingAssistantFiles] = useState(false);
  
  // Add responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [publicLinkCopied, setPublicLinkCopied] = useState(false);
  
  useEffect(() => {
    const fetchBotDetails = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBot(response.data);
        
        // Get knowledge base data
        const knowledgeResponse = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${id}/knowledge`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setKnowledgeFiles(knowledgeResponse.data.files || []);
        setWebsites(knowledgeResponse.data.websites || []);
        
        // After loading bot data, fetch assistant files if available
        if (response.data.assistantId) {
          await fetchAssistantFiles(response.data.id);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching bot details:', error);
        setLoading(false);
        setSnackbar({
          open: true,
          message: 'Error loading bot details',
          severity: 'error'
        });
      }
    };
    
    fetchBotDetails();
  }, [id, token]);
  
  // Update the fetchAssistantFiles function
  const fetchAssistantFiles = async (botId) => {
    setLoadingAssistantFiles(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${botId}/assistant-files`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Set the assistant files
      setAssistantFiles(response.data.files || []);
      
      // Log the vector stores for debugging
      if (response.data.vectorStores && response.data.vectorStores.length > 0) {
        console.log('Vector stores attached to assistant:', response.data.vectorStores);
      }
      
    } catch (error) {
      console.error('Error fetching assistant files:', error);
      setSnackbar({
        open: true,
        message: 'Error loading assistant files',
        severity: 'error'
      });
    } finally {
      setLoadingAssistantFiles(false);
    }
  };
  
  // Add a function to fetch vector store details
  const fetchVectorStoreDetails = async (vectorStoreId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${id}/vector-stores/${vectorStoreId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching vector store details for ${vectorStoreId}:`, error);
      return null;
    }
  };
  
  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    setSnackbar({
      open: true,
      message: 'Embed code copied to clipboard!',
      severity: 'success'
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleDeleteConfirmation = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleDeleteBot = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbar({
        open: true,
        message: 'Bot deleted successfully',
        severity: 'success'
      });
      
      // Redirect to dashboard after brief delay
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error) {
      console.error('Error deleting bot:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting bot',
        severity: 'error'
      });
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };
  
  // File upload handlers
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setFileError('');
  };
  
  const handleUploadDialog = () => {
    setUploadDialogOpen(true);
    setSelectedFile(null);
    setFileError('');
  };
  
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
  };
  
  const handleFileUpload = async () => {
    if (!selectedFile) {
      setFileError('Please select a file to upload');
      return;
    }
    
    // Check file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setFileError('File size must be less than 10MB');
      return;
    }
    
    // Check file type
    const allowedTypes = [
      'application/pdf', 
      'text/plain', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/csv',
      'application/json'
    ];
    
    if (!allowedTypes.includes(selectedFile.type)) {
      setFileError('File type not supported. Please upload PDF, TXT, DOCX, CSV, or JSON files');
      return;
    }
    
    setUploadingFile(true);
    setUploadProgress(0);
    
    // Create form data
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${id}/knowledge/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      // Add the new file to the list
      setKnowledgeFiles(prev => [...prev, response.data]);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'File uploaded successfully',
        severity: 'success'
      });
      
      // Close dialog
      setUploadDialogOpen(false);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      setFileError(error.response?.data?.message || 'Failed to upload file. Please try again.');
    } finally {
      setUploadingFile(false);
    }
  };
  
  // Delete knowledge base file
  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${id}/knowledge/${fileId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove file from list
      setKnowledgeFiles(prev => prev.filter(file => file.id !== fileId));
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'File removed from knowledge base',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Error deleting file:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to remove file',
        severity: 'error'
      });
    }
  };
  
  // Website scraping handlers
  const handleWebsiteDialog = () => {
    setWebsiteDialogOpen(true);
    setWebsiteUrl('');
    setWebsiteError('');
  };
  
  const handleCloseWebsiteDialog = () => {
    setWebsiteDialogOpen(false);
  };
  
  const handleWebsiteUrlChange = (e) => {
    setWebsiteUrl(e.target.value);
    setWebsiteError('');
  };
  
  const handleWebsiteScrape = async () => {
    // Basic URL validation
    if (!websiteUrl.trim()) {
      setWebsiteError('Please enter a website URL');
      return;
    }
    
    // Simple URL validation
    if (!websiteUrl.match(/^(http|https):\/\/[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i)) {
      setWebsiteError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }
    
    setScrapingWebsite(true);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${id}/knowledge/scrape`,
        { url: websiteUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add website to list
      setWebsites(prev => [...prev, response.data]);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Website content added to knowledge base',
        severity: 'success'
      });
      
      // Close dialog
      setWebsiteDialogOpen(false);
      
    } catch (error) {
      console.error('Error scraping website:', error);
      setWebsiteError(error.response?.data?.message || 'Failed to scrape website. Please try again.');
    } finally {
      setScrapingWebsite(false);
    }
  };
  
  // Delete website from knowledge base
  const handleDeleteWebsite = async (websiteId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${id}/knowledge/website/${websiteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove website from list
      setWebsites(prev => prev.filter(site => site.id !== websiteId));
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Website removed from knowledge base',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Error deleting website:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to remove website',
        severity: 'error'
      });
    }
  };
  
  // Train bot handler
  const handleTrainBot = async () => {
    setTrainingBot(true);
    setError(""); // Now setError is defined
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${id}/knowledge/train`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTrainingComplete(true);
      setSnackbar({
        open: true,
        message: 'Bot trained successfully with knowledge base content',
        severity: 'success'
      });
      
      // Show files and websites processed
      console.log(`Training complete: ${response.data.filesProcessed} files and ${response.data.websitesProcessed} websites processed`);
      
    } catch (error) {
      console.error('Error training bot:', error);
      
      // Extract detailed error message if available
      const errorMessage = error.response?.data?.message || 
                          (error.response?.data?.error ? 
                           `OpenAI error: ${error.response.data.error}` : 
                           'Failed to train bot. Please check your OpenAI API key configuration.');
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      setTrainingComplete(false);
    } finally {
      setTrainingBot(false);
    }
  };

  // Add these helper functions before the main BotDetails component return statement
  const getVectorStores = (files) => {
    // Group files by vector collection ID
    const storeMap = new Map();
    
    // First pass - collect files into vector stores
    files.forEach(file => {
      if (file.vectorCollectionId) {
        if (!storeMap.has(file.vectorCollectionId)) {
          storeMap.set(file.vectorCollectionId, {
            id: file.vectorCollectionId,
            files: [file],
            totalDocuments: file.vectorDocumentCount || 0
          });
        } else {
          const store = storeMap.get(file.vectorCollectionId);
          store.files.push(file);
          store.totalDocuments += (file.vectorDocumentCount || 0);
        }
      }
    });
    
    return Array.from(storeMap.values());
  };
  
  // Check if a vector store is attached to the assistant
  const isVectorStoreAttached = (vectorStoreId, assistantFiles) => {
    if (!vectorStoreId || !assistantFiles || !Array.isArray(assistantFiles)) return false;
    
    // Direct check for vector stores in files array
    if (assistantFiles.some(file => file.vectorStoreId === vectorStoreId)) {
      return true;
    }
    
    // Fallback to the previous checks
    return assistantFiles.some(file => {
      if (file?.vectorStoreId === vectorStoreId) return true;
      
      if (file?.tool_resources?.vector_stores && 
          Array.isArray(file.tool_resources.vector_stores)) {
        return file.tool_resources.vector_stores.includes(vectorStoreId);
      }
      
      if (file?.vector_store_ids && Array.isArray(file.vector_store_ids)) {
        return file.vector_store_ids.includes(vectorStoreId);
      }
      
      return false;
    });
  };

  // Add a helper function to extract vector stores from assistant files
  const getVectorStoresFromAssistant = (assistantFiles) => {
    if (!assistantFiles || !Array.isArray(assistantFiles)) return [];
    
    // Direct detection of vector stores in files array
    const vectorStoresFromFiles = assistantFiles.filter(file => file.vectorStoreId);
    
    if (vectorStoresFromFiles.length > 0) {
      return vectorStoresFromFiles;
    }
    
    // Fallback to the old code
    const vectorStoreIds = new Set();
    
    assistantFiles.forEach(file => {
      if (file?.vectorStoreId) {
        vectorStoreIds.add(file.vectorStoreId);
      }
      
      if (file?.tool_resources?.vector_stores && 
          Array.isArray(file.tool_resources.vector_stores)) {
        file.tool_resources.vector_stores.forEach(id => vectorStoreIds.add(id));
      }
      
      if (file?.vector_store_ids && Array.isArray(file.vector_store_ids)) {
        file.vector_store_ids.forEach(id => vectorStoreIds.add(id));
      }
    });
    
    return Array.from(vectorStoreIds).map(id => ({ id, vectorStoreId: id }));
  };

  const generateEmbedCode = (botId) => {
    const scriptSrc = `https://gorillaforge-ai-server.onrender.com/embed/chatbot.js`;
    return {
      wordpress: `<div id="chatbox-container"></div>
<script src="${scriptSrc}" data-bot-id="${botId}" defer></script>`,
      express: `
app.use('/chatbot', express.static('path/to/chatbot.js'));
app.get('/chatbot', (req, res) => {
  res.send('<div id="chatbox-container"></div><script src="${scriptSrc}" data-bot-id="${botId}" defer></script>');
});
      `,
      node: `
const http = require('http');
const fs = require('fs');

http.createServer((req, res) => {
  if (req.url === '/chatbot') {
    res.writeHead(200, { 'Content-Type': 'text/html' });
    res.end('<div id="chatbox-container"></div><script src="${scriptSrc}" data-bot-id="${botId}" defer></script>');
  } else {
    res.writeHead(404);
    res.end();
  }
}).listen(3000, () => console.log('Server running on port 3000'));
      `,
    };
  };

  const handleCopyPublicLink = () => {
    const baseUrl = window.location.origin;
    const publicUrl = `${baseUrl}/public/${id}`;
    navigator.clipboard.writeText(publicUrl);
    
    setPublicLinkCopied(true);
    setTimeout(() => setPublicLinkCopied(false), 3000);
    
    setSnackbar({
      open: true,
      message: 'Public link copied to clipboard!',
      severity: 'success'
    });
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #121212 0%, #2c3e50 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Add animated background elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z' fill='%23ffffff' fill-opacity='0.3' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }}
        />
        
        {/* Enhanced loading spinner with animated ring */}
        <Box
          sx={{
            position: 'relative',
            width: 100,
            height: 100,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '3px solid rgba(255, 255, 255, 0.1)',
              borderTopColor: theme.palette.primary.main,
              animation: 'spin 1s linear infinite',
            }}
          />
          <CircularProgress size={60} sx={{ 
            color: 'white',
            opacity: 0.8,
          }} />
          
          {/* Pulsing bot icon in center */}
          <Box
            sx={{
              position: 'absolute',
              animation: 'pulse 2s infinite ease-in-out'
            }}
          >
            <SmartToyIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #121212 0%, #2c3e50 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Circuit board pattern background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z' fill='%23ffffff' fill-opacity='0.3' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          zIndex: 0
        }}
      />

      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 0
        }}
      >
        {/* Flowing particles */}
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: ['100px', '150px', '200px', '250px', '300px', '350px'][i],
              height: ['100px', '150px', '200px', '250px', '300px', '350px'][i],
              background: `rgba(76, 133, 240, ${0.03 + (i % 3) * 0.01})`,
              borderRadius: '50%',
              top: `${10 + i * 15}%`,
              left: `${5 + i * 15}%`,
              animation: `float ${5 + i * 2}s infinite ease-in-out alternate`,
              opacity: 0.4,
            }}
          />
        ))}

        {/* Top-right gradient blob */}
        <Box
          sx={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '500px',
            height: '500px',
            background: `radial-gradient(circle, ${bot?.customizations?.primaryColor || theme.palette.primary.main}22, transparent 70%)`,
            opacity: 0.4,
            filter: 'blur(80px)',
            zIndex: 0,
            animation: 'pulse 15s infinite alternate'
          }}
        />
      </Box>
      
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, position: 'relative', zIndex: 1 }}>
        {/* Back button and Header - Enhanced */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            position: 'relative'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              component={Link}
              to="/"
              startIcon={<ArrowBackIcon />}
              sx={{ 
                color: 'white', 
                mr: 2,
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '0%',
                  height: '2px',
                  backgroundColor: 'white',
                  transition: 'width 0.3s ease-in-out'
                },
                '&:hover::after': {
                  width: '100%'
                }
              }}
            >
              Back
            </Button>
            <Typography 
              variant="h4" 
              component="h1" 
              color="white" 
              fontWeight="bold"
              sx={{ 
                animation: 'fadeIn 0.8s forwards',
                position: 'relative'
              }}
            >
              {bot?.name}
              {/* Decorative element */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '-10px',
                  left: '0',
                  width: '60px',
                  height: '4px',
                  background: `linear-gradient(90deg, ${bot?.customizations?.primaryColor || theme.palette.primary.main}, transparent)`,
                  borderRadius: '2px'
                }}
              />
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              component={Link}
              to={`/bots/${id}/test`}
              variant="contained"
              startIcon={<WebIcon />}
              sx={{ 
                background: `linear-gradient(90deg, ${bot?.customizations?.primaryColor || theme.palette.primary.main}, ${bot?.customizations?.primaryColor || theme.palette.secondary.main})`,
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.3)'
                }
              }}
            >
              Test Website
            </Button>

            <Button
              component={Link}
              to={`/bots/${id}/edit`}
              variant="outlined"
              startIcon={<EditIcon />}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.05)'
                }
              }}
            >
              Edit
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteConfirmation}
              sx={{
                borderColor: 'rgba(244,67,54,0.5)',
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: 'rgba(244,67,54,0.08)',
                  borderColor: theme.palette.error.main
                }
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
        
        {/* Bot info card - Enhanced with glass morphism */}
        <Paper 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            background: 'rgba(30, 35, 45, 0.7)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            animation: 'fadeInUp 0.8s forwards',
            overflow: 'visible',
            position: 'relative'
          }}
        >
          {/* Decorative corner accent */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `radial-gradient(circle at top right, ${bot?.customizations?.primaryColor || theme.palette.primary.main}40, transparent 70%)`,
              opacity: 0.8,
              borderRadius: '0 16px 0 100%',
              zIndex: 0
            }}
          />
          
          <Grid container spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
            {/* Bot avatar/icon - Enhanced */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box
                sx={{
                  position: 'relative',
                  animation: 'fadeIn 0.8s forwards 0.2s',
                  opacity: 0
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 150, 
                    height: 150, 
                    backgroundColor: bot.customizations?.primaryColor || theme.palette.primary.main,
                    boxShadow: `0 8px 20px ${bot.customizations?.primaryColor || theme.palette.primary.main}40`,
                    border: '4px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  {bot.avatar ? (
                    <img 
                      src={bot.avatar} 
                      alt={bot.name} 
                      style={{ width: '100%', height: '100%' }} 
                    />
                  ) : (
                    <Typography 
                      variant="h1" 
                      sx={{ 
                        color: 'white', 
                        textTransform: 'uppercase',
                        fontWeight: 'bold'
                      }}
                    >
                      {bot.name.charAt(0)}
                    </Typography>
                  )}
                </Avatar>
                
                {/* Pulsing ring */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: `2px solid ${bot.customizations?.primaryColor || theme.palette.primary.main}50`,
                    animation: 'pulseRing 3s infinite ease-out'
                  }}
                />
              </Box>
            </Grid>
            
            {/* Bot details - Enhanced with animations */}
            <Grid item xs={12} md={9}>
              <Typography 
                variant="h5" 
                component="h2" 
                sx={{ 
                  mb: 1, 
                  fontWeight: 'bold',
                  color: 'white',
                  animation: 'fadeInUp 0.5s forwards',
                  opacity: 0
                }}
              >
                {bot.name}
              </Typography>
              
              <Typography 
                variant="body1" 
                paragraph
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  animation: 'fadeInUp 0.5s forwards 0.1s',
                  opacity: 0
                }}
              >
                {bot.description}
              </Typography>
              
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 2, 
                  mt: 2,
                  animation: 'fadeInUp 0.5s forwards 0.2s',
                  opacity: 0
                }}
              >
                <Chip 
                  label={`Personality: ${bot.personality ? (bot.personality.charAt(0).toUpperCase() + bot.personality.slice(1)) : 'Default'}`}
                  sx={{ 
                    backgroundColor: 'rgba(76, 133, 240, 0.15)', 
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                />
                
                <Chip 
                  label={`Model: ${bot.preferredModel || 'Default'}`}
                  sx={{ 
                    backgroundColor: 'rgba(76, 133, 240, 0.15)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                />
                
                <Chip
                  avatar={
                    <Avatar 
                      sx={{ 
                        bgcolor: bot.customizations?.primaryColor || theme.palette.primary.main,
                        width: 24,
                        height: 24
                      }}
                    />
                  }
                  label="Primary Color"
                  sx={{ 
                    backgroundColor: 'rgba(76, 133, 240, 0.15)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Tabs - Enhanced with glowing effects */}
        <Grid container spacing={3} sx={{ 
          animation: 'fadeInUp 0.8s forwards 0.3s',
          opacity: 0
        }}>
          {/* Left column: Summary Card with Shortcut Buttons */}
          <Grid item xs={12} md={4} lg={3}>
            <Paper
              sx={{ 
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                background: 'rgba(30, 35, 45, 0.7)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Bot Stats Summary */}
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ 
                  color: 'white', 
                  mb: 2, 
                  position: 'relative',
                  pb: 1,
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: '40px',
                    height: '3px',
                    background: bot?.customizations?.primaryColor || theme.palette.primary.main,
                    borderRadius: '2px'
                  }
                }}>
                  Bot Summary
                </Typography>

                {/* Stats Grid */}
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Box sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                      p: 2,
                      borderRadius: 2,
                      textAlign: 'center',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }}>
                      <StorageIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }} />
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 'medium' }}>
                        {knowledgeFiles.length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Files
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                      p: 2,
                      borderRadius: 2,
                      textAlign: 'center',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }}>
                      <LanguageIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }} />
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 'medium' }}>
                        {websites.length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Websites
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Bot Details */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 1.5
                  }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Model
                    </Typography>
                    <Chip 
                      label={bot.preferredModel || 'Default'} 
                      size="small" 
                      sx={{ 
                        backgroundColor: 'rgba(76, 133, 240, 0.15)', 
                        color: 'white',
                        borderRadius: '4px',
                      }}
                    />
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 1.5
                  }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Personality
                    </Typography>
                    <Chip 
                      label={bot.personality ? (bot.personality.charAt(0).toUpperCase() + bot.personality.slice(1)) : 'Default'} 
                      size="small" 
                      sx={{ 
                        backgroundColor: 'rgba(76, 133, 240, 0.15)', 
                        color: 'white',
                        borderRadius: '4px',
                      }}
                    />
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                  }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Status
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: bot.assistantId ? '#66BB6A' : '#FFC107',
                          mr: 1,
                          animation: bot.assistantId ? 'pulse 1.5s infinite' : 'none'
                        }} 
                      />
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {bot.assistantId ? 'Active' : 'Pending Setup'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Shortcut Buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Button
                    component={Link}
                    to={`/bots/${id}/test`}
                    variant="contained"
                    fullWidth
                    startIcon={<WebIcon />}
                    sx={{ 
                      background: `linear-gradient(90deg, ${bot?.customizations?.primaryColor || theme.palette.primary.main}, ${bot?.customizations?.primaryColor || theme.palette.secondary.main})`,
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                      py: 1.2,
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.3)'
                      }
                    }}
                  >
                    Test Website
                  </Button>

                  {/* Add Public Link Button */}
                  <Button
                    onClick={handleCopyPublicLink}
                    variant="outlined"
                    fullWidth
                    startIcon={publicLinkCopied ? <CheckIcon /> : <ShareIcon />}
                    sx={{ 
                      color: publicLinkCopied ? 'success.main' : 'white',
                      borderColor: publicLinkCopied ? 'success.main' : 'rgba(255,255,255,0.3)',
                      '&:hover': {
                        borderColor: publicLinkCopied ? 'success.main' : 'white',
                        backgroundColor: 'rgba(255,255,255,0.05)'
                      }
                    }}
                  >
                    {publicLinkCopied ? 'Link Copied!' : bot.isPublic ? 'Share Public Link' : 'Copy Private Link'}
                  </Button>

                  <Button
                    onClick={() => setTabValue(1)}
                    variant="outlined"
                    fullWidth
                    startIcon={<CodeIcon />}
                    sx={{ 
                      color: 'white', 
                      borderColor: 'rgba(255,255,255,0.3)',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.05)'
                      }
                    }}
                  >
                    Get Embed Code
                  </Button>

                  <Button
                    onClick={handleTrainBot}
                    variant="outlined"
                    fullWidth
                    disabled={trainingBot || (knowledgeFiles.length === 0 && websites.length === 0)}
                    startIcon={trainingBot ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                    sx={{ 
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.05)'
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255,255,255,0.3)',
                        borderColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    {trainingBot ? 'Training...' : 'Train Bot'}
                  </Button>

                
                </Box>
              </Box>

              {/* Section for Assistant ID if it exists */}
              {bot.assistantId && (
                <Box sx={{ 
                  p: 3,
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  mt: 'auto'
                }}>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                    OpenAI Assistant ID
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'white', 
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}
                    >
                      {bot.assistantId}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        navigator.clipboard.writeText(bot.assistantId);
                        setSnackbar({
                          open: true,
                          message: 'Assistant ID copied to clipboard!',
                          severity: 'success'
                        });
                      }}
                      sx={{ color: 'rgba(255,255,255,0.7)' }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
          
          {/* Right column: Tabs Box */}
          <Grid item xs={12} md={8} lg={9}>
            <Paper 
              sx={{ 
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                background: 'rgba(30, 35, 45, 0.7)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                overflow: 'hidden'
              }}
            >
              <Tabs 
                value={tabValue} 
                onChange={handleChangeTab}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    py: 2,
                    color: 'rgba(255, 255, 255, 0.7)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '0%',
                      height: '3px',
                      backgroundColor: bot.customizations?.primaryColor || theme.palette.primary.main,
                      transition: 'width 0.3s ease'
                    },
                    '&:hover': {
                      color: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      '&::after': {
                        width: '30%'
                      }
                    },
                    '&.Mui-selected': {
                      color: 'white',
                      '&::after': {
                        width: '70%'
                      }
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'transparent'
                  }
                }}
              >
                <Tab icon={<ChatIcon />} label="Preview" />
                <Tab icon={<CodeIcon />} label="Embed Code" />
                <Tab icon={<SettingsIcon />} label="Settings" />
                <Tab icon={<ArticleIcon />} label="Knowledge Base" />
              </Tabs>
              
              <Box sx={{ p: 3 }}>
                {/* Existing tabs content */}
                {/* Preview Tab */}
                <Box hidden={tabValue !== 0}>
                  {bot && <ChatPreview bot={bot} />}
                </Box>
                
                {/* Embed Code Tab */}
                <Box hidden={tabValue !== 1}>
                  <Card variant="outlined" sx={{ mb: 4 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Embed Code</Typography>
                        <Button 
                          variant="outlined" 
                          startIcon={<CopyIcon />} 
                          onClick={() => {
                            const embedCode = generateEmbedCode(bot.id);
                            navigator.clipboard.writeText(embedCode.wordpress);
                            setSnackbar({
                              open: true,
                              message: 'WordPress embed code copied to clipboard!',
                              severity: 'success'
                            });
                          }}
                        >
                          Copy WordPress Code
                        </Button>
                      </Box>
                      
                      <Typography variant="subtitle1" gutterBottom>WordPress</Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={generateEmbedCode(bot.id).wordpress}
                        InputProps={{ readOnly: true }}
                        multiline
                        rows={3}
                        sx={{ mb: 3 }}
                      />

                      <Typography variant="subtitle1" gutterBottom>Express.js</Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={generateEmbedCode(bot.id).express}
                        InputProps={{ readOnly: true }}
                        multiline
                        rows={6}
                        sx={{ mb: 3 }}
                      />

                      <Typography variant="subtitle1" gutterBottom>Node.js</Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={generateEmbedCode(bot.id).node}
                        InputProps={{ readOnly: true }}
                        multiline
                        rows={6}
                      />
                    </CardContent>
                  </Card>
                </Box>
                
                {/* Settings Tab */}
                <Box hidden={tabValue !== 2}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Bot Information</Typography>
                          <Typography variant="body2" paragraph>
                            <strong>Name:</strong> {bot.name}
                          </Typography>
                          <Typography variant="body2" paragraph>
                            <strong>Description:</strong> {bot.description}
                          </Typography>
                          <Typography variant="body2" paragraph>
                            <strong>Personality:</strong> {bot.personality}
                          </Typography>
                          <Typography variant="body2" paragraph>
                            <strong>AI Model:</strong> {bot.preferredModel}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Customization</Typography>
                          <Typography variant="body2" paragraph>
                            <strong>Primary Color:</strong> {bot.customizations?.primaryColor}
                          </Typography>
                          <Typography variant="body2" paragraph>
                            <strong>Font Family:</strong> {bot.customizations?.fontFamily}
                          </Typography>
                          <Typography variant="body2" paragraph>
                            <strong>Position:</strong> {bot.customizations?.position}
                          </Typography>
                          <Typography variant="body2" paragraph>
                            <strong>Welcome Message:</strong> {bot.customizations?.welcomeMessage}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button
                      component={Link}
                      to={`/bots/${id}/edit`}
                      variant="contained"
                      color="primary"
                      startIcon={<EditIcon />}
                      sx={{ mt: 2 }}
                    >
                      Edit Bot Settings
                    </Button>
                  </Box>
                </Box>
                
                {/* Knowledge Base Tab */}
                <Box hidden={tabValue !== 3}>
                  <Box mb={3}>
                    <Alert severity={trainingComplete ? "success" : "info"} sx={{ mb: 2 }}>
                      {trainingComplete 
                        ? "Bot training is complete! Your bot is now using vector stores for enhanced knowledge retrieval."
                        : "Upload files to create vector embeddings and enhance your bot's knowledge. Then train your bot to apply the new knowledge."}
                    </Alert>
                    
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleTrainBot}
                        disabled={trainingBot || (knowledgeFiles.length === 0 && websites.length === 0)}
                        startIcon={trainingBot ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{
                          py: 1.2,
                          px: 3,
                          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        }}
                      >
                        {trainingBot ? 'Training...' : 'Train Bot with Knowledge Base'}
                      </Button>
                    </Box>
                  </Box>
                  
                  <Grid container spacing={3}>
                    {/* Files Section */}
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Knowledge Files</Typography>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<CloudUploadIcon />}
                              onClick={handleUploadDialog}
                            >
                              Upload File
                            </Button>
                          </Box>
                          
                          {knowledgeFiles.length === 0 ? (
                            <Box textAlign="center" py={4}>
                              <UploadFileIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                              <Typography color="text.secondary">
                                No files uploaded yet. Upload files to enhance your bot's knowledge.
                              </Typography>
                            </Box>
                          ) : (
                            <List>
                              {knowledgeFiles.map((file) => (
                                <ListItem key={file.id} divider>
                                  <ListItemText
                                    primary={file.name || 'Unknown file'}
                                    secondary={
                                      <>
                                        {/* More robust type checking */}
                                        {`${(file.type && typeof file.type === 'string' && file.type.includes('/')) 
                                            ? file.type.split('/')[1].toUpperCase()
                                            : 'UNKNOWN'} • ${file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : 'Unknown date'}`}
                                        {file.vectorCollectionId && (
                                          <Chip 
                                            label={`${file.vectorDocumentCount || 'N/A'} Vectors`} 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                            sx={{ ml: 1 }}
                                          />
                                        )}
                                        {assistantFiles.some(af => af?.knowledgeFileId === file.id) && (
                                          <Chip 
                                            label="Attached" 
                                            size="small" 
                                            color="success" 
                                            sx={{ ml: 1 }}
                                          />
                                        )}
                                      </>
                                    }
                                  />
                                  <ListItemSecondaryAction>
                                    <IconButton edge="end" onClick={() => handleDeleteFile(file.id)} color="error">
                                      <DeleteIcon />
                                    </IconButton>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* Assistant Files Section */}
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Assistant Files</Typography>
                            <Chip 
                              label={bot.assistantId ? "Assistant Active" : "No Assistant"}
                              color={bot.assistantId ? "success" : "warning"}
                              variant="outlined"
                            />
                          </Box>
                          
                          {loadingAssistantFiles ? (
                            <Box display="flex" justifyContent="center" my={4}>
                              <CircularProgress size={40} />
                            </Box>
                          ) : assistantFiles.length === 0 ? (
                            <Box textAlign="center" py={4}>
                              <ArticleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                              <Typography color="text.secondary">
                                {bot.assistantId 
                                  ? "No files attached to the assistant. Train your bot to attach files." 
                                  : "Create an assistant first by training your bot."}
                              </Typography>
                            </Box>
                          ) : (
                            <List>
                              {/* Vector Stores attached to the assistant */}
                              {getVectorStoresFromAssistant(assistantFiles).length > 0 && (
                                <ListItem>
                                  <ListItemText
                                    primary={
                                      <Box display="flex" alignItems="center">
                                        <Typography variant="subtitle1">Vector Stores</Typography>
                                        <Chip 
                                          label={`${getVectorStoresFromAssistant(assistantFiles).length} Connected`} 
                                          size="small" 
                                          color="success" 
                                          variant="outlined" 
                                          sx={{ ml: 1 }}
                                        />
                                      </Box>
                                    }
                                    secondary={
                                      <List dense disablePadding sx={{ mt: 1, pl: 2 }}>
                                        {getVectorStoresFromAssistant(assistantFiles).map(vectorStore => (
                                          <ListItem key={vectorStore.id || vectorStore.vectorStoreId} sx={{ py: 0.5 }}>
                                            <ListItemText
                                              primary={
                                                <Typography variant="body2">
                                                  {vectorStore.name || (vectorStore.id || vectorStore.vectorStoreId 
                                                    ? <Box component="span" 
                                                        sx={{ 
                                                          display: 'inline-flex', 
                                                          alignItems: 'center',
                                                          '&:before': {
                                                            content: '""',
                                                            display: 'inline-block',
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            bgcolor: vectorStore.error ? 'warning.main' : 'success.main',
                                                            mr: 1
                                                          }
                                                        }}
                                                      >
                                                        {`${(vectorStore.id || vectorStore.vectorStoreId).substring(0, 10)}...`}
                                                      </Box>
                                                    : 'Unknown'
                                                  )}
                                                </Typography>
                                              }
                                              secondary={
                                                <Typography variant="caption" color="text.secondary">
                                                  {vectorStore.status && `Status: ${vectorStore.status} • `}
                                                  {vectorStore.fileIds ? `${vectorStore.fileIds.length} files` : 
                                                   knowledgeFiles.filter(f => f.vectorCollectionId === (vectorStore.id || vectorStore.vectorStoreId)).length || 0} files
                                                </Typography>
                                              }
                                            />
                                          </ListItem>
                                        ))}
                                      </List>
                                    }
                                  />
                                </ListItem>
                              )}
                              
                              {/* Regular files attached to the assistant */}
                              {assistantFiles
                                .filter(file => file.id && !file.vectorStoreId)
                                .map((file) => (
                                  <ListItem key={file.id || 'unknown'} divider>
                                    <ListItemText
                                      primary={file.name || 'Unnamed file'}
                                      secondary={
                                        <>
                                          {`File ID: ${file.id ? file.id.substring(0, 10) : 'Unknown'}... • ${file.createdAt ? new Date(file.createdAt * 1000).toLocaleDateString() : 'Unknown date'}`}
                                        </>
                                      }
                                    />
                                  </ListItem>
                              ))}
                            </List>
                          )}
                        </CardContent>
                        <Box p={2} borderTop="1px solid" borderColor="divider">
                          <Typography variant="caption" color="text.secondary">
                            Files that are currently attached to the OpenAI Assistant and available for use in conversations.
                            {bot.assistantId && (
                              <Box mt={1}>Assistant ID: {bot.assistantId}</Box>
                            )}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>

                    {/* Websites Section - Existing code */}
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Website Content</Typography>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<LanguageIcon />}
                              onClick={handleWebsiteDialog}
                            >
                              Add Website
                            </Button>
                          </Box>
                          
                          {websites.length === 0 ? (
                            <Box textAlign="center" py={4}>
                              <WebIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                              <Typography color="text.secondary">
                                No websites added yet. Add website URLs to enhance your bot's knowledge.
                              </Typography>
                            </Box>
                          ) : (
                            <List>
                              {websites.map((site) => (
                                <ListItem key={site.id} divider>
                                  <ListItemText
                                    primary={site.url || 'Unknown URL'}
                                    secondary={`${site.pages || 0} pages • ${site.scrapedAt ? new Date(site.scrapedAt).toLocaleDateString() : 'Unknown date'}`}
                                  />
                                  <ListItemSecondaryAction>
                                    <IconButton 
                                      edge="end" 
                                      onClick={() => handleDeleteWebsite(site.id)} 
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Vector Stores Section - Enhanced */}
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Vector Stores</Typography>
                            <Chip 
                              label={`${getVectorStores(knowledgeFiles).length} Stores`}
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
    
                          {getVectorStores(knowledgeFiles).length === 0 ? (
                            <Box textAlign="center" py={4}>
                              <UpdateDisabledIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                              <Typography color="text.secondary">
                                No vector stores available. Upload files and train your bot to create vector stores.
                              </Typography>
                            </Box>
                          ) : (
                            <List>
                              {getVectorStores(knowledgeFiles).map((vectorStore) => (
                                <React.Fragment key={vectorStore.id}>
                                  <ListItem divider>
                                    <ListItemText
                                      primary={
                                        <Box display="flex" alignItems="center" gap={1}>
                                          <Typography variant="subtitle1">
                                            Vector Store: {vectorStore.id ? vectorStore.id.substring(0, 10) + '...' : 'Unknown ID'}
                                          </Typography>
                                          {isVectorStoreAttached(vectorStore.id, assistantFiles) ? (
                                            <Chip 
                                              label="Connected to Assistant" 
                                              size="small" 
                                              color="success" 
                                              variant="outlined" 
                                              sx={{ fontWeight: 'medium' }}
                                            />
                                          ) : (
                                            <Chip 
                                              label="Not Connected" 
                                              size="small" 
                                              color="default" 
                                              variant="outlined" 
                                              sx={{ opacity: 0.7 }}
                                            />
                                          )}
                                        </Box>
                                      }
                                      secondary={
                                        <>
                                          <Typography variant="body2" color="text.secondary">
                                            Total Documents: {vectorStore.totalDocuments}
                                          </Typography>
                                          <Box mt={1}>
                                            <Typography variant="body2" fontWeight="medium">
                                              Files in this vector store:
                                            </Typography>
                                            <List dense disablePadding>
                                              {vectorStore.files.map(file => (
                                                <ListItem key={file.id} sx={{ pl: 0, py: 0.5 }}>
                                                  <ListItemText 
                                                    primary={file.name || 'Unnamed file'}
                                                    secondary={
                                                      <Typography variant="caption" color="text.secondary">
                                                        {file.type && typeof file.type === 'string' && file.type.includes('/') ? 
                                                          file.type.split('/')[1].toUpperCase() : 'UNKNOWN'} • 
                                                        {file.vectorDocumentCount ? `${file.vectorDocumentCount} vectors` : 'Unknown count'}
                                                      </Typography>
                                                    }
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                  />
                                                </ListItem>
                                              ))}
                                            </List>
                                          </Box>
                                        </>
                                      }
                                    />
                                  </ListItem>
                                </React.Fragment>
                              ))}
                            </List>
                          )}
                        </CardContent>
                        <Box p={2} borderTop="1px solid" borderColor="divider">
                          <Typography variant="caption" color="text.secondary">
                            Vector stores provide enhanced semantic search capabilities for your assistant.
                            {bot.assistantId && (
                              <Box mt={1}>When a vector store is connected to your assistant, it can search through documents more effectively.</Box>
                            )}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                    
                    {/* The existing explanation card */}
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>How the Knowledge Base Works</Typography>
                          <Typography variant="body2" paragraph>
                            Files and websites added to the knowledge base are processed and indexed to enhance 
                            your bot's responses. When users interact with your bot, it will use this additional 
                            information to provide more accurate and relevant answers.
                          </Typography>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Typography variant="subtitle1" gutterBottom>Supported File Types:</Typography>
                          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                            <Chip label="PDF" size="small" />
                            <Chip label="Text" size="small" />
                            <Chip label="Word (DOCX)" size="small" />
                            <Chip label="CSV" size="small" />
                            <Chip label="JSON" size="small" />
                          </Box>
                          
                          <Typography variant="subtitle1" gutterBottom>File Limits:</Typography>
                          <Typography variant="body2">
                            Each file must be less than 10MB. For best results, use structured, text-rich documents.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      
      {/* File Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog}>
        <DialogTitle>Upload Knowledge File</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Upload files like PDFs, documents, or text files to enhance your bot's knowledge base.
            Files will be processed and used to improve your bot's responses.
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              fullWidth
            >
              Select File
              <input
                type="file"
                hidden
                onChange={handleFileChange}
                accept=".pdf,.txt,.docx,.csv,.json"
              />
            </Button>
            
            {selectedFile && (
              <Box mt={2} p={2} border="1px dashed" borderColor="divider" borderRadius={1}>
                <Typography variant="body2">
                  <strong>Selected file:</strong> {selectedFile.name || 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedFile.size ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedFile.type 
                    ? `Type: ${typeof selectedFile.type === 'string' && selectedFile.type.includes('/') 
                          ? selectedFile.type.split('/')[1].toUpperCase() 
                          : 'FILE'}` 
                    : ''}
                </Typography>
              </Box>
            )}
            
            {fileError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {fileError}
              </Alert>
            )}
            
            {uploadingFile && (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Uploading: {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog} disabled={uploadingFile}>
            Cancel
          </Button>
          <Button 
            onClick={handleFileUpload} 
            variant="contained" 
            color="primary"
            disabled={!selectedFile || uploadingFile}
            startIcon={uploadingFile ? <CircularProgress size={16} /> : null}
          >
            {uploadingFile ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Website URL Dialog */}
      <Dialog open={websiteDialogOpen} onClose={handleCloseWebsiteDialog}>
        <DialogTitle>Add Website to Knowledge Base</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Enter a website URL to scrape its content. The bot will extract information from the website
            to enhance its responses.
          </Typography>
          
          <TextField
            fullWidth
            label="Website URL"
            variant="outlined"
            value={websiteUrl}
            onChange={handleWebsiteUrlChange}
            margin="normal"
            placeholder="https://example.com"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LanguageIcon />
                </InputAdornment>
              )
            }}
            error={!!websiteError}
            helperText={websiteError}
          />
          
          {scrapingWebsite && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Scraping website content...
              </Typography>
              <LinearProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWebsiteDialog} disabled={scrapingWebsite}>
            Cancel
          </Button>
          <Button 
            onClick={handleWebsiteScrape} 
            variant="contained" 
            color="primary"
            disabled={!websiteUrl.trim() || scrapingWebsite}
            startIcon={scrapingWebsite ? <CircularProgress size={16} /> : null}
          >
            {scrapingWebsite ? 'Processing...' : 'Add Website'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Delete Bot</Typography>
          <Typography variant="body1">
            Are you sure you want to delete "{bot?.name}"? This action cannot be undone.
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={handleCancelDelete} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteBot} 
              variant="contained" 
              color="error"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={18} /> : null}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </Box>
        </Box>
      </Dialog>
      
      {/* Snackbar for notifications - Enhanced */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiAlert-root': {
            backdropFilter: 'blur(10px)',
            background: snackbar.severity === 'success' 
              ? 'rgba(76, 175, 80, 0.9)' 
              : snackbar.severity === 'error'
                ? 'rgba(244, 67, 54, 0.9)'
                : 'rgba(33, 150, 243, 0.9)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* CSS for animations */}
      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translateY(0) rotate(0deg);
          }
          100% {
            transform: translateY(-20px) rotate(10deg);
          }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulseRing {
          0% { transform: scale(1); opacity: 0.6; }
          70% { opacity: 0; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        
        @keyframes glowCard {
          0%, 100% { box-shadow: 0 0 15px rgba(76, 133, 240, 0.3); }
          50% { box-shadow: 0 0 25px rgba(76, 133, 240, 0.6); }
        }
      `}</style>
    </Box>
  );
};

export default BotDetails;